import * as THREE from 'three';
import { modelLoader } from '../systems/ModelLoader.js';
import { PhysicsSystem } from '../systems/PhysicsSystem.js';
import { GAME_BOUNDARY_SIZE } from '../utils/constants.js';

const TEMP_VEC3_A = new THREE.Vector3();
const TEMP_VEC3_B = new THREE.Vector3();
const TEMP_VEC3_C = new THREE.Vector3();

const PROJECTILE_SPEED = 26;
const PROJECTILE_RADIUS = 0.55;
const MAX_ACTIVE_PROJECTILES = 4;
const PROJECTILE_GEOMETRY = new THREE.SphereGeometry(PROJECTILE_RADIUS, 12, 12);
const ALLY_PROJECTILE_MATERIAL = new THREE.MeshLambertMaterial({ color: 0xdddddd });
const ENEMY_PROJECTILE_MATERIAL = new THREE.MeshLambertMaterial({ color: 0xff6666 });

export class EscortBoat {
    constructor({ game, manager, attackPower = 8, formationIndex = 0, ownerShip = null, isEnemyEscort = false }) {
        this.game = game;
        this.manager = manager;
        this.attackPower = attackPower;
        this.formationIndex = formationIndex;
        this.ownerShip = ownerShip; // 所属主船（玩家或敌船）
        this.isEnemyEscort = isEnemyEscort; // 是否为敌船副船

        this.mesh = null;
        this.body = null;
        this.id = null;
        this.faction = isEnemyEscort ? 'enemy' : 'player';
        this.isEscort = true;

        this.followLerp = 0.18;
        this.verticalOffset = 0.1;

        this.speedMultiplier = 2.4;
        this.baseMaxSpeed = 36;
        this.acceleration = 120;
        this.damping = 0.32;
        this.velocity = new THREE.Vector3();

        this.stuckTimer = 0;
        this.stuckTimeout = 1.0; // 缩短卡住检测时间，更快脱困
        this.stuckSpeedThreshold = 0.8;
        this.minStuckDistance = 4.5;
        this.maxStuckDistance = 15; // 增加瞬移距离，避免再次卡住

        this.shootingRange = 24;
        // 敌船副船射击频率降低到玩家副船的三分之一（提升玩家生存能力）
        this.shootingInterval = isEnemyEscort ? 5.4 : 1.8;
        this.shootCooldown = 0.8;
        this.targetCheckCooldown = 0;
        this.target = null;

        this.projectileSpeed = PROJECTILE_SPEED; // 用于计算命中延迟和速度
        this.activeProjectiles = new Set();

        this.formationOffset = new THREE.Vector2(0, -4.5);
        this.isDisposed = false;

        this.lastPosition = new THREE.Vector3();
        this.actualSpeed = 0;
    }

    async init(startPosition) {
        const boatModel = await modelLoader.loadModel('ship/small-1', {
            scale: 0.5,
            position: startPosition ? { x: startPosition.x, y: startPosition.y, z: startPosition.z } : { x: 0, y: 0, z: 0 },
            rotation: { x: 0, y: 0, z: 0 },
            castShadow: true,
            receiveShadow: true
        });

        this.mesh = boatModel;
        // 根据阵营设置mesh的userData
        const meshType = this.isEnemyEscort ? 'enemy' : 'ally';
        this.mesh.userData = { type: meshType, entity: this };
        this.mesh.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });

        this.createPhysicsBody(startPosition);
        this.setFormationIndex(this.formationIndex);
        this.snapToFormationInstant();
    }

    createPhysicsBody(position = null) {
        const spawnPosition = position
            ? new THREE.Vector3(position.x, position.y, position.z)
            : new THREE.Vector3(0, this.verticalOffset, 0);
        spawnPosition.y = this.verticalOffset;

        const size = { width: 2.4, height: 0.9, length: 4.8 };
        this.body = this.game.physicsSystem.createShipBody(spawnPosition, size);

        if (!this.body) {
            return;
        }

        this.body.mass = 60;
        this.body.updateMassProperties();
        this.body.linearDamping = 0.45;
        this.body.angularDamping = 0.9;
        this.body.collisionFilterGroup = 2; // 所有船只都是碰撞组2
        this.body.collisionFilterMask = 1; // 只与边界(1)碰撞，不与船只(2)、炮弹(4)、岛屿/建筑(8)碰撞
        this.body.position.y = this.verticalOffset;

        // 复制主船的朝向（玩家或敌船）
        const ownerShip = this.ownerShip || this.game.player;
        if (ownerShip?.body?.quaternion) {
            this.body.quaternion.copy(ownerShip.body.quaternion);
        }

        // 根据阵营设置body的userData
        const bodyType = this.isEnemyEscort ? 'enemy' : 'ally';
        this.body.userData = { type: bodyType, entity: this };

        this.lastPosition.copy(this.body.position);
    }

    setFormationIndex(index) {
        this.formationIndex = index;
        const offset = this.manager?.getFormationOffset(index);
        if (offset) {
            this.formationOffset.copy(offset);
        } else {
            this.formationOffset.set(0, -4.5 - index * 1.25);
        }

        this.snapToFormationInstant();
    }

    update(deltaTime) {
        if (this.isDisposed || !this.body) {
            return;
        }

        // 检查主船是否存在
        const ownerShip = this.ownerShip || this.game?.player;
        if (!ownerShip || !ownerShip.body || ownerShip.health <= 0 || ownerShip.shouldRemove) {
            // 主船已死亡或被移除，标记副船为应该移除
            this.shouldRemove = true;
            return;
        }

        this.updateFollow(deltaTime);
        this.updateCombat(deltaTime);

        PhysicsSystem.syncMeshWithBody(this.mesh, this.body);
    }

    updateFollow(deltaTime) {
        // 获取主船（玩家或敌船）
        const ownerShip = this.ownerShip || this.game.player;
        if (!ownerShip || !ownerShip.body) {
            this.shouldRemove = true;
            return;
        }

        // 如果主船已经死亡或被标记移除，副船也应该被移除
        if (ownerShip.health <= 0 || ownerShip.shouldRemove) {
            this.shouldRemove = true;
            return;
        }

        const playerPos = ownerShip.body.position;
        const playerQuat = ownerShip.body.quaternion;

        const forward = TEMP_VEC3_A.set(0, 0, 1);
        forward.applyQuaternion(playerQuat);
        forward.normalize();

        const right = TEMP_VEC3_B.set(1, 0, 0);
        right.applyQuaternion(playerQuat);
        right.normalize();

        const desired = TEMP_VEC3_C.copy(playerPos);
        desired.x += forward.x * this.formationOffset.y + right.x * this.formationOffset.x;
        desired.y = this.verticalOffset;
        desired.z += forward.z * this.formationOffset.y + right.z * this.formationOffset.x;

        let clampedDelta = deltaTime;
        if (!Number.isFinite(clampedDelta) || clampedDelta <= 0) {
            clampedDelta = 0.016;
        } else {
            clampedDelta = Math.min(0.1, Math.max(0.008, clampedDelta));
        }

        const current = this.body.position;
        const actualDeltaX = current.x - this.lastPosition.x;
        const actualDeltaZ = current.z - this.lastPosition.z;
        const actualDistance = Math.hypot(actualDeltaX, actualDeltaZ);
        this.actualSpeed = clampedDelta > 0 ? actualDistance / clampedDelta : 0;

        const offsetX = desired.x - current.x;
        const offsetZ = desired.z - current.z;
        const distance = Math.hypot(offsetX, offsetZ);

        const playerConfiguredSpeed = ownerShip.speed ?? ownerShip.maxVelocity ?? 15;
        const maxSpeed = Math.max(this.baseMaxSpeed, playerConfiguredSpeed * this.speedMultiplier);

        let desiredVelX = 0;
        let desiredVelZ = 0;
        if (distance > 0.001) {
            const dirX = offsetX / distance;
            const dirZ = offsetZ / distance;
            const catchUpSpeed = Math.min(maxSpeed, distance / clampedDelta);
            desiredVelX = dirX * catchUpSpeed;
            desiredVelZ = dirZ * catchUpSpeed;

            if (distance < 8) {
                const slowFactor = Math.max(0.25, distance / 8);
                desiredVelX *= slowFactor;
                desiredVelZ *= slowFactor;
            }
        }

        const requestedSpeed = Math.hypot(desiredVelX, desiredVelZ);

        this.velocity.set(this.body.velocity.x, 0, this.body.velocity.z);

        const velDeltaX = desiredVelX - this.velocity.x;
        const velDeltaZ = desiredVelZ - this.velocity.z;
        const maxSpeedChange = this.acceleration * clampedDelta;
        const changeLength = Math.hypot(velDeltaX, velDeltaZ);

        if (changeLength > maxSpeedChange && changeLength > 0) {
            const scale = maxSpeedChange / changeLength;
            this.velocity.x += velDeltaX * scale;
            this.velocity.z += velDeltaZ * scale;
        } else {
            this.velocity.x += velDeltaX;
            this.velocity.z += velDeltaZ;
        }

        const dampingFactor = Math.max(0, 1 - this.damping * clampedDelta);
        this.velocity.x *= dampingFactor;
        this.velocity.z *= dampingFactor;

        const planarSpeed = Math.hypot(this.velocity.x, this.velocity.z);

        if (distance < 0.3 && (planarSpeed < 0.4 || this.actualSpeed < 0.2)) {
            this.velocity.set(0, 0, 0);
        }

        this.body.velocity.x = this.velocity.x;
        this.body.velocity.y = 0;
        this.body.velocity.z = this.velocity.z;

        const actualSpeedThreshold = this.stuckSpeedThreshold * 0.5;
        const isTooFar = distance > this.minStuckDistance;
        const isTryingToMove = requestedSpeed > this.stuckSpeedThreshold;
        const isBarelyMoving = this.actualSpeed < actualSpeedThreshold;

        if (isTooFar && isTryingToMove && isBarelyMoving) {
            this.stuckTimer += clampedDelta;
        } else if (distance < 2.5 || !isTryingToMove || this.actualSpeed >= actualSpeedThreshold) {
            this.stuckTimer = 0;
        } else {
            this.stuckTimer = Math.max(0, this.stuckTimer - clampedDelta);
        }

        if (this.stuckTimer >= this.stuckTimeout) {
            this.forceSnapToFormation();
            this.stuckTimer = 0;
        }

        current.y = this.verticalOffset;

        if (current.x < -GAME_BOUNDARY_SIZE) {
            current.x = -GAME_BOUNDARY_SIZE;
            if (this.body.velocity.x < 0) this.body.velocity.x = 0;
        } else if (current.x > GAME_BOUNDARY_SIZE) {
            current.x = GAME_BOUNDARY_SIZE;
            if (this.body.velocity.x > 0) this.body.velocity.x = 0;
        }

        if (current.z < -GAME_BOUNDARY_SIZE) {
            current.z = -GAME_BOUNDARY_SIZE;
            if (this.body.velocity.z < 0) this.body.velocity.z = 0;
        } else if (current.z > GAME_BOUNDARY_SIZE) {
            current.z = GAME_BOUNDARY_SIZE;
            if (this.body.velocity.z > 0) this.body.velocity.z = 0;
        }

        if (ownerShip?.body?.quaternion) {
            this.body.quaternion.copy(ownerShip.body.quaternion);
        }

        this.lastPosition.copy(current);
    }

    // Forcefully reposition escort when obstructed for too long
    forceSnapToFormation() {
        // 瞬移到更远的位置，避免再次卡住
        const retreatDistance = Math.min(this.maxStuckDistance * 0.6, 8);
        this.teleportToFormation(retreatDistance, 1.0);
    }

    snapToFormationInstant() {
        this.teleportToFormation(0, 0);
    }

    teleportToFormation(retreatDistance = 0, lateralShift = 0) {
        const ownerShip = this.ownerShip || this.game.player;
        // 增强检查：确保主船物理体完全准备好
        if (!ownerShip?.body || !this.body || !ownerShip.body.position || !ownerShip.body.quaternion) {
            console.warn('副船瞬移失败：主船或副船物理体未准备好');
            return;
        }

        const playerPos = ownerShip.body.position;
        const playerQuat = ownerShip.body.quaternion;

        const forward = TEMP_VEC3_A.set(0, 0, 1);
        forward.applyQuaternion(playerQuat);
        forward.normalize();

        const right = TEMP_VEC3_B.set(1, 0, 0);
        right.applyQuaternion(playerQuat);
        right.normalize();

        const target = TEMP_VEC3_C.copy(playerPos);
        target.x += forward.x * this.formationOffset.y + right.x * this.formationOffset.x;
        target.y = this.verticalOffset;
        target.z += forward.z * this.formationOffset.y + right.z * this.formationOffset.x;

        if (retreatDistance !== 0) {
            target.x += -forward.x * retreatDistance;
            target.z += -forward.z * retreatDistance;
        }

        if (lateralShift !== 0) {
            target.x += right.x * lateralShift;
            target.z += right.z * lateralShift;
        }

        target.x = THREE.MathUtils.clamp(target.x, -GAME_BOUNDARY_SIZE, GAME_BOUNDARY_SIZE);
        target.z = THREE.MathUtils.clamp(target.z, -GAME_BOUNDARY_SIZE, GAME_BOUNDARY_SIZE);

        this.body.position.set(target.x, this.verticalOffset, target.z);
        this.body.velocity.set(0, 0, 0);
        if (this.body.angularVelocity) {
            this.body.angularVelocity.set(0, 0, 0);
        }
        this.velocity.set(0, 0, 0);

        if (ownerShip?.body?.quaternion) {
            this.body.quaternion.copy(ownerShip.body.quaternion);
        }

        if (typeof this.body.wakeUp === 'function') {
            this.body.wakeUp();
        }

        this.lastPosition.copy(this.body.position);
        this.actualSpeed = 0;
    }

    updateCombat(deltaTime) {
        this.shootCooldown = Math.max(0, this.shootCooldown - deltaTime);
        this.targetCheckCooldown -= deltaTime;

        if (this.targetCheckCooldown <= 0) {
            this.target = this.findTarget();
            this.targetCheckCooldown = 0.6;
        }

        if (this.target && this.shootCooldown <= 0) {
            this.fireAt(this.target);
            this.shootCooldown = this.shootingInterval;
        }
    }

    findTarget() {
        const sharedTarget = this.resolveSharedPlayerTarget();
        if (sharedTarget) {
            return sharedTarget;
        }

        // 如果是敌船副船，攻击玩家主船或其他敌船主船
        if (this.isEnemyEscort) {
            return this.findEnemyEscortTarget();
        }

        // 玩家副船攻击敌船主船
        return this.findNearestAvailableEnemy();
    }

    resolveSharedPlayerTarget() {
        const ownerShip = this.ownerShip || this.game.player;
        if (!ownerShip || typeof ownerShip.getCurrentAttackTarget !== 'function' || !this.body) {
            return null;
        }

        const target = ownerShip.getCurrentAttackTarget();
        if (!this.isTargetEngageable(target)) {
            return null;
        }

        const targetPos = target.body?.position || target.mesh?.position;
        if (!targetPos) {
            return null;
        }

        const origin = this.body.position;
        const dx = targetPos.x - origin.x;
        const dz = targetPos.z - origin.z;
        const maxLinkRange = this.shootingRange * 1.4;

        if ((dx * dx + dz * dz) > maxLinkRange * maxLinkRange) {
            return null;
        }

        return target;
    }

    findNearestAvailableEnemy() {
        const enemies = this.game.enemyManager?.enemies;
        if (!enemies || enemies.size === 0 || !this.body) {
            return null;
        }

        const origin = this.body.position;
        let closest = null;
        let closestDistSq = this.shootingRange * this.shootingRange;

        enemies.forEach((enemy) => {
            // 只攻击敌船主船，不攻击敌船副船
            if (!this.isTargetEngageable(enemy) || enemy.isEscort) return;

            const pos = enemy.body.position;
            const dx = pos.x - origin.x;
            const dz = pos.z - origin.z;
            const distSq = dx * dx + dz * dz;
            if (distSq < closestDistSq) {
                closestDistSq = distSq;
                closest = enemy;
            }
        });

        return closest;
    }

    // 敌船副船寻找玩家主船或其他敌船主船作为目标（不攻击副船）
    findEnemyEscortTarget() {
        if (!this.body) return null;

        const origin = this.body.position;
        let closest = null;
        let closestDistSq = this.shootingRange * this.shootingRange;

        // 1. 检查玩家主船
        const player = this.game.player;
        if (player && player.body && player.health > 0) {
            const playerPos = player.body.position;
            const dx = playerPos.x - origin.x;
            const dz = playerPos.z - origin.z;
            const distSq = dx * dx + dz * dz;

            if (distSq <= closestDistSq) {
                closest = player;
                closestDistSq = distSq;
            }
        }

        // 2. 检查其他敌船主船（不包括自己的主船和副船）
        const enemies = this.game.enemyManager?.enemies;
        if (enemies && enemies.size > 0) {
            enemies.forEach((enemy) => {
                // 跳过自己的主船、副船、已死亡的
                if (!enemy || enemy.health <= 0 || !enemy.body || enemy.isEscort) return;
                if (enemy === this.ownerShip) return; // 不攻击自己的主船

                const pos = enemy.body.position;
                const dx = pos.x - origin.x;
                const dz = pos.z - origin.z;
                const distSq = dx * dx + dz * dz;

                if (distSq < closestDistSq) {
                    closestDistSq = distSq;
                    closest = enemy;
                }
            });
        }

        return closest;
    }

    isTargetEngageable(target) {
        if (!target) {
            return false;
        }

        if (target.health <= 0 || target.shouldRemove) {
            return false;
        }

        if (!target.body) {
            return false;
        }

        return true;
    }

    fireAt(target) {
        if (!target?.body || !this.game) return;

        // 清理已删除的项目符号引用
        this.pruneProjectiles();

        if (this.activeProjectiles.size >= MAX_ACTIVE_PROJECTILES) {
            return;
        }

        const origin = new THREE.Vector3();
        this.mesh.getWorldPosition(origin);
        origin.y = this.verticalOffset + 0.3;

        const direction = new THREE.Vector3(
            target.body.position.x - origin.x,
            (target.body.position.y + 0.2) - origin.y,
                target.body.position.z - origin.z
        ).normalize();

        const spawn = origin.clone().add(direction.clone().multiplyScalar(1.8));
        this.spawnProjectile(spawn, direction);
    }

    pruneProjectiles() {
        if (!this.activeProjectiles) return;
        this.activeProjectiles.forEach(projectile => {
            if (projectile.shouldRemove) {
                this.activeProjectiles.delete(projectile);
            }
        });
    }

    spawnProjectile(position, direction) {
        if (!this.game) return;

        const material = this.isEnemyEscort ? ENEMY_PROJECTILE_MATERIAL : ALLY_PROJECTILE_MATERIAL;
        const mesh = new THREE.Mesh(PROJECTILE_GEOMETRY, material);
        mesh.position.copy(position);
        mesh.castShadow = false;

        const velocity = direction.clone().multiplyScalar(this.projectileSpeed);

        const projectileId = `escort_proj_${this.id}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        const projectile = {
            id: projectileId,
            mesh,
            velocity,
            radius: PROJECTILE_RADIUS,
            life: 0,
            maxLife: 2.5,
            owner: this,
            shouldRemove: false,
            update: (deltaTime) => {
                if (projectile.shouldRemove) return;

                projectile.life += deltaTime;
                if (projectile.life >= projectile.maxLife || this.isDisposed) {
                    projectile.shouldRemove = true;
                    return;
                }

                mesh.position.addScaledVector(projectile.velocity, deltaTime);

                const hitTarget = this.findProjectileHit(mesh.position, projectile.radius);
                if (hitTarget) {
                    this.applyProjectileDamage(hitTarget, mesh.position);
                    projectile.shouldRemove = true;
                }
            },
            dispose: () => {
                this.activeProjectiles.delete(projectile);
            }
        };

        this.activeProjectiles.add(projectile);
        this.game.addGameObject(projectileId, projectile);
    }

    findProjectileHit(position, radius) {
        if (this.isEnemyEscort) {
            const player = this.game.player;
            if (player && player.body && player.health > 0) {
                if (position.distanceToSquared(player.body.position) <= (radius + 2.8) ** 2) {
                    return player;
                }
            }

            const enemies = this.game.enemyManager?.enemies;
            if (enemies) {
                for (const enemy of enemies.values()) {
                    if (!enemy || enemy.health <= 0 || !enemy.body || enemy === this.ownerShip || enemy.isEscort) continue;
                    if (position.distanceToSquared(enemy.body.position) <= (radius + 2.8) ** 2) {
                        return enemy;
                    }
                }
            }
            return null;
        }

        const enemies = this.game.enemyManager?.enemies;
        if (!enemies) return null;

        for (const enemy of enemies.values()) {
            if (!enemy || enemy.health <= 0 || !enemy.body || enemy.isEscort) continue;
            if (position.distanceToSquared(enemy.body.position) <= (radius + 2.8) ** 2) {
                return enemy;
            }
        }
        return null;
    }

    applyProjectileDamage(target, impactPosition) {
        if (!target || typeof target.takeDamage !== 'function') return;

        target.takeDamage(this.attackPower, this.ownerShip || this);
        const explosionSource = this.game?.player ?? this.ownerShip;
        explosionSource?.createExplosion?.(impactPosition.clone());
    }

    dispose() {
        if (this.isDisposed) return;
        this.isDisposed = true;

        // 从场景中移除mesh
        if (this.mesh) {
            if (this.game?.sceneManager) {
                this.game.sceneManager.remove(this.mesh);
            }
            this.mesh.traverse((child) => {
                if (child.geometry) child.geometry.dispose?.();
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(mat => mat.dispose?.());
                    } else {
                        child.material.dispose?.();
                    }
                }
            });
            this.mesh = null;
        }

        // 从物理世界移除body
        if (this.body) {
            if (this.game?.physicsSystem) {
                this.game.physicsSystem.removeBody(this.body);
            }
            this.body = null;
        }

        this.activeProjectiles?.forEach(projectile => {
            projectile.shouldRemove = true;
            this.game?.removeGameObject?.(projectile.id);
        });
        this.activeProjectiles?.clear();

        this.manager?.handleFollowerDestroyed(this);
    }
}
